import { NgxEchartsModule } from 'ngx-echarts';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { BarSeries } from '../../models/comparisson.types';
import { formatChemicalUnit } from '../../../../shared/utils/format-unit';

export type BarChartTheme = 'blue' | 'green' | 'orange' | 'default';

@Component({
  selector: 'app-bar-chart',
  templateUrl: './bar-chart.component.html',
  imports: [NgxEchartsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BarChartComponent {
  readonly title = input<string>('');
  readonly yUnit = input<string>('');
  readonly categories = input.required<string[]>();
  readonly series = input.required<BarSeries[]>();

  readonly theme = input<BarChartTheme>('default');

  private readonly palettes: Record<BarChartTheme, string[]> = {
    default: ['#0092BC', '#7AC6DC'],
    blue: ['#0092BC', '#7AC6DC'],
    green: ['#4C9F70', '#9BC53D'],
    orange: ['#FF9F1C', '#FFD18A'],
  };

  readonly options = computed(() => {
    const categories = this.categories();
    const series = this.series();
    const yUnit = formatChemicalUnit(this.yUnit());
    const palette = this.palettes[this.theme()];

    return {
      animation: true,
      animationDuration: 600,
      grid: { left: 40, right: 24, top: 24, bottom: 64 },

      legend: {
        bottom: 8,
        left: 'center',
        itemWidth: 14,
        itemHeight: 10,
        textStyle: { fontSize: 13, color: '#6b7280' },
        data: series.map(s => s.name),
      },

      xAxis: {
        type: 'category',
        data: categories,
        axisLabel: { color: '#5B6770' },
        axisLine: { lineStyle: { color: '#e5e7eb' } },
      },

      yAxis: {
        type: 'value',
        axisLabel: { color: '#9ca3af' },
        axisLine: { show: false },
        splitLine: { lineStyle: { color: '#e5e7eb', type: 'dashed' } },
      },

      series: series.map((s, seriesIndex) => ({
        name: s.name,
        type: 'bar',
        stack: s.stack,
        data: s.data,
        itemStyle: {
          color: palette[seriesIndex % palette.length],
          opacity: 0.8,
        },
        barWidth: '45%',
      })),

      tooltip: {
        trigger: 'axis',
        backgroundColor: '#ffffff',
        borderColor: '#e5e7eb',
        borderWidth: 1,
        borderRadius: 8,
        padding: [12, 16],
        extraCssText: 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);',
        formatter: (params: unknown): string => {
          const items = params as {
            axisValue?: string;
            seriesName?: string;
            value?: number;
            color?: string;
          }[];

          const axis = items[0]?.axisValue ?? '';
          const rows = items
            .map((item: { axisValue?: string; seriesName?: string; value?: number; color?: string }) => {
              const value = item?.value ?? 0;
              return `
                    <div style="display:flex; justify-content:space-between; gap:12px;">
                        <span style="color:#6b7280;">${item.seriesName}</span>
                        <span style="font-weight:600; color:${item.color};">${value}${yUnit ? '&nbsp;' + yUnit : ''}</span>
                    </div>
                    `;
            })
            .join('');

          return `
                <div style="font-size: 14px; font-weight: 600; color: #374151;">
                ${axis}
                </div>
                <div style="margin-top: 10px;">
                ${rows}
                </div>
            `;
        },
      },
    };
  });
}
